import { Inject } from '@wendellhu/redi';
import { IHttpServiceToken, type IHttpService } from "@/backend/core";
import { createTable, listTable, updateTable } from "@/backend/core/clients/nocobase.client";
import type { ISolutionRepository } from "../interfaces/solution.repository.interface";
import type {
  CaseMaindataArchiveDTO,
  EngrEngineeringDTO,
  EngrVersionDTO,
  MdpsProductionOrderDTO,
  TasksDTO,
} from "@holoview/dao-gen/generated";
import type {
  ActiveSolutionCardsQueryParams,
  CompletedTaskDispatchCountQueryParams,
  SolutionAppendsQueryParams,
  SolutionCreatePayload,
  SolutionImagingParseStateDto,
  SolutionPlanSetupUpdatePayload,
  SolutionWithCase,
} from "../dto/solution.dto";

type EngrEngineeringRaw = EngrEngineeringDTO & {
  engr_imaging_parse_state?: number | string | null;
};

type HomeSolutionCardRow = Record<string, unknown> & {
  id?: number | string;
  case_maindata_id?: number | string | null;
  case_maindata?: { id?: number | string | null };
};

function buildTaskDispatchParticipationFilter(userId: number): Record<string, unknown> {
  return {
    $or: [
      {
        task_dispatchs: {
          created_by_id: { $eq: userId },
        },
      },
      {
        task_dispatchs: {
          accept_user_id: { $eq: userId },
        },
      },
    ],
  };
}

function buildHomeSolutionCardsFilter(
  params: ActiveSolutionCardsQueryParams,
): Record<string, unknown> {
  const currentUserId = params.currentUserId;
  const tab = params.tab ?? 'mine';

  if (tab === 'done') {
    const completedByUserIds = params.completedByUserIds ?? [];
    return {
      tasks: {
        status: { $eq: 'completed' },
        current_task_dispatch: {
          completed_by_user_id: { $in: completedByUserIds },
        },
      },
    };
  }

  if (tab === 'collab') {
    return buildTaskDispatchParticipationFilter(currentUserId);
  }

  return {
    $and: [
      { status: { $ne: 'finish' } },
      {
        tasks: {
          status: { $eq: 'processing' },
          current_task_dispatch: {
            accept_user_id: { $eq: currentUserId },
          },
        },
      },
    ],
  };
}

function compactText(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

function combineAnd(filters: Record<string, unknown>[]): Record<string, unknown> {
  if (filters.length === 0) return {};
  if (filters.length === 1) return filters[0];
  return { $and: filters };
}

function buildHomeSolutionRemoteFilter(
  params: ActiveSolutionCardsQueryParams,
  baseFilter: Record<string, unknown> = buildHomeSolutionCardsFilter(params),
): Record<string, unknown> {
  const filters: Record<string, unknown>[] = [baseFilter];
  const filter = params.filter;
  if (!filter) return combineAnd(filters);

  const keyword = compactText(filter.keyword);
  if (keyword) {
    const keywordFilters: Record<string, unknown>[] = [
      { name: { $includes: keyword } },
      { 'case_maindata.name': { $includes: keyword } },
      { 'case_maindata.extend.name': { $includes: keyword } },
    ];
    const engineeringId = parseHomeEngineeringId(keyword);
    if (engineeringId != null) {
      keywordFilters.push({ id: { $eq: engineeringId } });
    }
    filters.push({ $or: keywordFilters });
  }

  const hospital = compactText(filter.hospital);
  if (hospital) {
    filters.push({
      $or: [
        { 'case_maindata.hospital.name': { $includes: hospital } },
        { 'case_maindata.hospital.fullname': { $includes: hospital } },
      ],
    });
  }

  const department = compactText(filter.department);
  if (department) {
    filters.push({ 'case_maindata.hospital_department.name': { $includes: department } });
  }

  const doctor = compactText(filter.doctor);
  if (doctor) {
    filters.push({
      $or: [
        { 'case_maindata.doctor.name': { $includes: doctor } },
        { 'case_maindata.doctor.show_name': { $includes: doctor } },
      ],
    });
  }

  const treatmentPlan = compactText(filter.treatmentPlan);
  if (treatmentPlan) {
    filters.push({
      $or: [
        { 'case_maindata.extend.treatment_plan': { $includes: treatmentPlan } },
        { 'case_maindata.extend.treatmentPlan': { $includes: treatmentPlan } },
      ],
    });
  }

  const updatedAt: Record<string, string> = {};
  if (filter.startDate) updatedAt.$gte = `${filter.startDate} 00:00:00`;
  if (filter.endDate) updatedAt.$lte = `${filter.endDate} 23:59:59`;
  if (Object.keys(updatedAt).length > 0) {
    filters.push({ updatedAt });
  }

  return combineAnd(filters);
}

function parseHomeEngineeringId(value: string): number | null {
  const match = value.match(/^(?:p\.\s*)?(\d+)$/i);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function uniqueEngineeringIdsFromTasks(rows: TasksDTO[]): number[] {
  return [...new Set(
    rows
      .map(row => Number(row.engr_engineering_id ?? 0))
      .filter(id => Number.isFinite(id) && id > 0),
  )];
}

export class SolutionRepository implements ISolutionRepository {
  constructor(@Inject(IHttpServiceToken) private readonly http: IHttpService) {}

  async getCaseMeta(solutionId: number): Promise<SolutionWithCase | null> {
    const result = await this.http.get<{ data: SolutionWithCase[] }>(
      "engr_engineering:list",
      {
        filter: JSON.stringify({ id: { $eq: solutionId } }),
        "appends[]": [
          "case_maindata",
          "case_maindata.status",
          "case_maindata.extend",
        ],
      },
    );
    const solution = (result.data ?? [])[0] ?? null;
    if (!solution?.case_maindata_id) return solution;

    const archiveResult = await this.http.get<{
      data: CaseMaindataArchiveDTO[];
    }>("case_maindata_archive:list", {
      filter: JSON.stringify({
        case_maindata_id: { $eq: solution.case_maindata_id },
      }),
      page: 1,
      pageSize: 1,
      sort: "-id",
    });
    const archive = (archiveResult.data ?? [])[0];
    if (archive) {
      solution.case_maindata = {
        id: solution.case_maindata_id,
        ...solution.case_maindata,
        case_maindata_archive: archive as unknown as Record<string, unknown>,
      };
    }
    return solution;
  }

  async getMdpsProductionOrderId(params: {
    solutionId: number;
    caseId?: number | null;
  }): Promise<number | null> {
    const bySolution = await this.findLatestMdpsProductionOrderId({
      engr_engineering_id: { $eq: params.solutionId },
    });
    if (bySolution) return bySolution;

    if (!params.caseId || params.caseId <= 0) return null;
    return this.findLatestMdpsProductionOrderId({
      case_maindata_id: { $eq: params.caseId },
    });
  }

  async findMdpsProductionOrderIds(solutionIds: number[]): Promise<Map<number, number>> {
    const ids = [...new Set(solutionIds.filter(id => Number.isFinite(id) && id > 0))];
    if (ids.length === 0) return new Map();

    const result = await this.http.get<{ data: MdpsProductionOrderDTO[] }>(
      "mdps_production_order:list",
      {
        filter: JSON.stringify({ engr_engineering_id: { $in: ids } }),
        page: 1,
        pageSize: ids.length,
        sort: "-id",
      },
    );
    const bySolutionId = new Map<number, number>();
    for (const order of result.data ?? []) {
      const solutionId = Number(order.engr_engineering_id ?? 0);
      const productionOrderId = Number(order.id ?? 0);
      if (solutionId > 0 && productionOrderId > 0 && !bySolutionId.has(solutionId)) {
        bySolutionId.set(solutionId, productionOrderId);
      }
    }
    return bySolutionId;
  }

  async getVersions(solutionId: number): Promise<EngrVersionDTO[]> {
    const result = await this.http.get<{ data: EngrVersionDTO[] }>(
      "engr_version:list",
      {
        filter: JSON.stringify({ engineering_id: { $eq: String(solutionId) } }),
        page: 1,
        pageSize: 9999,
        sort: "-version",
      },
    );
    return result.data ?? [];
  }

  async setImagingSelection(
    solutionId: number,
    _imagingId: number | null,
    activeSeriesId: string,
  ): Promise<void> {
    await this.http.post<void>(
      "engr_engineering:update",
      {
        series_id: activeSeriesId,
      },
      { params: { filterByTk: solutionId } },
    );
  }

  async updateStatus(versionId: number, status: string): Promise<void> {
    await this.http.post<void>(
      "engr_version:update",
      { status },
      { params: { filterByTk: versionId } },
    );
  }

  async updateEngineeringStatus(
    solutionId: number,
    status: string,
  ): Promise<void> {
    await this.http.post<void>(
      "engr_engineering:update",
      { status },
      { params: { filterByTk: solutionId } },
    );
  }

  async createVersion(data: {
    engineering_id: number;
    version: number;
    status: string;
  }): Promise<{ id: number }> {
    const result = await this.http.post<{
      data?: { id?: number };
      id?: number;
    }>("engr_version:create", data);
    const id = Number(result?.data?.id ?? result?.id ?? 0);
    if (!id) throw new Error("createVersion failed: no id returned");
    return { id };
  }

  async createSolution(
    params: SolutionCreatePayload,
  ): Promise<EngrEngineeringDTO> {
    const result = await createTable<EngrEngineeringDTO>(
      "engr_engineering",
      params,
      "",
    );
    return result.data;
  }

  async findSolutionsByCase(caseId: number): Promise<EngrEngineeringDTO[]> {
    const result = await listTable<EngrEngineeringDTO>("engr_engineering", {
      page: 1,
      pageSize: 9999,
      tree: false,
      filter: { case_maindata_id: { $eq: caseId } },
      "sort[]": ["-updatedAt", "-id"],
    });
    return result.data ?? [];
  }

  async updatePlanSetup(solutionId: number, params: SolutionPlanSetupUpdatePayload): Promise<void> {
    await updateTable<EngrEngineeringDTO>(
      "engr_engineering",
      solutionId,
      params,
    );
  }

  async getActiveSolutionCards(
    params: ActiveSolutionCardsQueryParams,
  ): Promise<Record<string, unknown>[]> {
    const appends = [
      "case_maindata",
      "createdBy",
      "updatedBy",
      "factor",
      "case_maindata.hospital",
      "case_maindata.hospital_department",
      "case_maindata.doctor",
      "case_maindata.site",
      "case_maindata.factors",
      "tasks",
      "tasks.current_task_dispatch",
      "tasks.current_task_dispatch.tasks_dispatch_targets",
      "case_maindata.extend",
      "case_maindata.extend.bodypart",
      "case_maindata.extend.indication",
      "case_maindata.extend.treatment_plan",
    ];
    const tab = params.tab ?? 'mine';
    const baseFilter = tab === 'collab'
      ? await this.buildCollabEngineeringIdFilter(params.currentUserId)
      : buildHomeSolutionCardsFilter(params);
    if (baseFilter == null) return [];

    const filter = JSON.stringify(buildHomeSolutionRemoteFilter(params, baseFilter));
    const result = await this.http.get<{ data: Record<string, unknown>[] }>(
      "engr_engineering:list",
      {
        page: 1,
        pageSize: 9999,
        filter,
        "appends[]": appends,
        "sort[]": ["-updatedAt"],
      },
    );
    return this.attachLatestMdpsProductionOrders(result.data ?? []);
  }

  private async buildCollabEngineeringIdFilter(currentUserId: number): Promise<Record<string, unknown> | null> {
    const result = await this.http.get<{ data?: TasksDTO[] }>(
      "tasks:list",
      {
        page: 1,
        pageSize: 9999,
        filter: JSON.stringify(buildTaskDispatchParticipationFilter(currentUserId)),
      },
    );
    const solutionIds = uniqueEngineeringIdsFromTasks(result.data ?? []);
    if (solutionIds.length === 0) return null;
    return {
      id: { $in: solutionIds },
    };
  }

  private async attachLatestMdpsProductionOrders(rows: Record<string, unknown>[]): Promise<Record<string, unknown>[]> {
    const normalized = rows as HomeSolutionCardRow[];
    const solutionIds = normalized.map(row => Number(row.id ?? 0) || 0).filter(id => id > 0);
    const caseIds = normalized
      .map(row => Number(row.case_maindata_id ?? row.case_maindata?.id ?? 0) || 0)
      .filter(id => id > 0);
    if (!solutionIds.length && !caseIds.length) return rows;

    const filter: Record<string, unknown>[] = [];
    if (solutionIds.length) filter.push({ engr_engineering_id: { $in: [...new Set(solutionIds)] } });
    if (caseIds.length) filter.push({ case_maindata_id: { $in: [...new Set(caseIds)] } });

    let orders: MdpsProductionOrderDTO[] = [];
    try {
      const result = await this.http.get<{ data: MdpsProductionOrderDTO[] }>(
        "mdps_production_order:list",
        {
          filter: JSON.stringify(filter.length === 1 ? filter[0] : { $or: filter }),
          page: 1,
          pageSize: Math.max(solutionIds.length + caseIds.length, 1),
          sort: "-id",
        },
      );
      orders = result.data ?? [];
    } catch {
      return rows;
    }

    const bySolutionId = new Map<number, MdpsProductionOrderDTO>();
    const byCaseId = new Map<number, MdpsProductionOrderDTO>();
    for (const order of orders) {
      const solutionId = Number(order.engr_engineering_id ?? 0) || 0;
      if (solutionId > 0 && !bySolutionId.has(solutionId)) bySolutionId.set(solutionId, order);
      const caseId = Number(order.case_maindata_id ?? 0) || 0;
      if (caseId > 0 && !byCaseId.has(caseId)) byCaseId.set(caseId, order);
    }

    return normalized.map(row => {
      const solutionId = Number(row.id ?? 0) || 0;
      const caseId = Number(row.case_maindata_id ?? row.case_maindata?.id ?? 0) || 0;
      const order = bySolutionId.get(solutionId) ?? byCaseId.get(caseId);
      return order ? { ...row, mdps_production_order: order } : row;
    });
  }

  async fetchSolutionsWithAppends(
    params: SolutionAppendsQueryParams,
  ): Promise<Record<string, unknown>[]> {
    const filterStr = params.filter ? JSON.stringify(params.filter) : undefined;
    const result = await this.http.get<{ data: Record<string, unknown>[] }>(
      "engr_engineering:list",
      {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 9999,
        tree: params.tree ?? false,
        ...(params.sort ? { sort: params.sort } : {}),
        ...(params.appends ? { "appends[]": params.appends } : {}),
        filter: filterStr,
      },
    );
    return result.data ?? [];
  }

  async countCompletedTaskDispatches(
    params: CompletedTaskDispatchCountQueryParams,
  ): Promise<number> {
    const completedAt: Record<string, string> = {};
    if (params.completedAtStart) completedAt.$gte = params.completedAtStart;
    if (params.completedAtEnd) completedAt.$lte = params.completedAtEnd;

    const completedByUserIds = params.completedByUserIds ?? [];
    const currentTaskDispatch: Record<string, unknown> = {
      completed_by_user_id: { $in: completedByUserIds },
    };
    if (Object.keys(completedAt).length > 0) {
      currentTaskDispatch.completed_at = completedAt;
    }

    const result = await this.http.get<{
      data?: TasksDTO[];
      meta?: { count?: number };
    }>("tasks:list", {
      page: 1,
      pageSize: 1,
      filter: JSON.stringify({
        current_task_dispatch: currentTaskDispatch,
      }),
    });
    return Number(result.meta?.count ?? 0) || 0;
  }

  async getImagingParseState(
    solutionId: number,
  ): Promise<SolutionImagingParseStateDto | null> {
    const result = await this.http.get<{ data: EngrEngineeringRaw[] }>(
      "engr_engineering:list",
      {
        filter: JSON.stringify({ id: { $eq: solutionId } }),
        page: 1,
        pageSize: 1,
      },
    );
    const row = (result.data ?? [])[0] ?? null;
    if (!row) return null;
    return {
      solutionId: Number(row.id ?? 0) || 0,
      imagingParseState: Number(row.engr_imaging_parse_state ?? 0) || 0,
    };
  }

  private async findLatestMdpsProductionOrderId(
    filter: Record<string, unknown>,
  ): Promise<number | null> {
    const result = await this.http.get<{ data: MdpsProductionOrderDTO[] }>(
      "mdps_production_order:list",
      {
        filter: JSON.stringify(filter),
        page: 1,
        pageSize: 1,
        sort: "-id",
      },
    );
    const id = Number((result.data ?? [])[0]?.id ?? 0);
    return id > 0 ? id : null;
  }
}
